import { Typography } from "antd";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import { euro } from "utils/formatters";

const { Paragraph } = Typography;

/**
 * A constant representing the error color.
 * This color is typically used to highlight error messages, warnings,
 * or invalid states in the user interface.
 * The value is a hex color code set to a shade of red (#ffdfdc).
 */
export const ERROR_COLOR = "#ffdfdc";

/**
 * A constant representing the reconciliation color.
 * This color is used to highlight reconciliation's columns.
 * The value is a hex color code set to a shade of blue (#ffdfdc).
 */
export const RECONCILIATION_COLOR = "#e9faff";

/**
 * A function that takes a string as input and returns a JSX element
 * rendering the given string within a Text component with ellipsis enabled.
 *
 * @param {string} stringToPrint - The string to be rendered within the Text component.
 * @returns {JSX.Element} A JSX element containing the rendered string, with ellipsis applied.
 */
export const printString = (stringToPrint: string) => {
  return <Paragraph
    style={{
      wordWrap: "break-word",
      wordBreak: "break-all",
      whiteSpace: "nowrap",
      maxWidth: 250
    }}
    ellipsis={{ tooltip: stringToPrint, rows: 1 }}
  >
    {stringToPrint}
  </Paragraph>;
};

export function formatCurrency(value: number | string | null) {
  const numValue = typeof value === "number" ? value : parseFloat(value as string);
  return !isNaN(numValue) ? euro(numValue) : "";
}

export function formatTmpPercentage(value: number | string | null) {
  const numValue = typeof value === "number" ? value : parseFloat(value as string);
  return !isNaN(numValue) ? `${numValue}%` : "";
}

/**
 * Processa e formatta un valore in base al suo tipo specificato.
 *
 * @param {number|string|null} value - Il valore da processare e formattare.
 * @param {string} valueType - Il tipo di valore da formattare. Può essere "text", "date", "number" o "currency".
 * @param {object} options - Opzioni aggiuntive per la formattazione.
 * @param {string} [options.dateTemplate="DD/MM/YYYY"] - Template di formattazione per le date.
 * @param {"D"|"P"|"E"} [options.status] - Stato del valore (D = Completato, P = In corso, E = Errore).
 * @returns {JSX.Element|null} Il valore formattato come elemento JSX, o null se non valido.
 */
export const printValue = (
  value: number | string | null | undefined,
  valueType: "text" | "date" | "number" | "currency",
  options?: {
    dateTemplate?: string;
    status?: "D" | "P" | "E";
    isSingle?: boolean
  },
): JSX.Element | null => {
  const dateTemplate = options?.dateTemplate || "DD/MM/YYYY";
  const status = options?.status;

  const EXTRACTION_IN_PROGRESS = "Estrazione in corso";
  const VALUE_NOT_EXTRACTED = "Valore non estratto";

  if (value === undefined) {
    return <></>;
  }

  // Gestione valore nullo o stringa vuota
  if (value === null || (typeof value === "string" && value.trim() === "")) {
    return status === "P"
      ? printString(EXTRACTION_IN_PROGRESS)
      : printString( (options?.isSingle === undefined || options.isSingle === true) ? VALUE_NOT_EXTRACTED : "");
  }

  // Gestione in base al tipo di valore specificato
  switch (valueType) {
    case "currency":
    case "number":
      // Assicuriamo che il valore sia un numero
      const numValue = typeof value === "number"
        ? value
        : parseFloat(value as string);

      // Verifichiamo che la conversione a numero sia valida
      if (!isNaN(numValue)) {
        return <>{euro(numValue)}</>;
      }
      return printString(value as string);

    case "date":
      // Gestiamo la formattazione delle date
      if (typeof value === "string") {
        if (
          dayjs(value, "YYYY-MM-DD", true).isValid() ||
          dayjs(value, "YYYY-MM", true).isValid() ||
          dayjs(value, { utc: true }, true).isValid()
        ) {
          return printString(capitalize(dayjs(value).format(dateTemplate)));
        }
      }
      return printString(String(value));

    case "text":
    default:
      // Per il testo, convertiamo semplicemente il valore in stringa
      return printString(String(value));
  }
};